/**
 * The contact rule, in one place.
 *
 * Two screens create or change a volunteer's email and phone — the staff add
 * form and the profile editor — and since 11 Sep 2026 both require them. Two
 * copies of the check would eventually disagree about what a phone number is,
 * and the disagreement would show up as a form that refuses what the one beside
 * it accepts.
 *
 * The email rule is imported from the shared package rather than written here
 * (9 Oct 2026). There were three copies of that regex — this one, one inside
 * the public registration form, and the API's — which is three chances to
 * disagree about what an address is, and the disagreement shows up as a form
 * that refuses what the one beside it accepts.
 *
 * The phone rule still mirrors `phoneish` in the API's validators, which is the
 * authority (§8.40). Lenient on purpose: a landline with an STD code is a real
 * way to reach somebody, and only the public intake insists on a 10-digit
 * mobile. This exists so the message arrives without a round trip.
 */

import { EMAIL_MESSAGE, isEmailish } from '@parinaam-vms/shared';

const PHONE_PATTERN = /^[+()\d][\d\s\-()]{5,19}$/;

/**
 * Field errors for a volunteer's contact details, keyed as the API keys them so
 * a caller can merge the result straight into its error state.
 */
export function contactProblems(email: string, phone: string): Record<string, string> {
  const problems: Record<string, string> = {};

  if (!email.trim()) {
    problems.email = 'Enter an email address.';
  } else if (!isEmailish(email)) {
    problems.email = EMAIL_MESSAGE;
  }

  if (!phone.trim()) {
    problems.phone = 'Enter a phone number.';
  } else if (!PHONE_PATTERN.test(phone.trim())) {
    problems.phone = 'Enter a phone number, digits only apart from + - ( ).';
  }

  return problems;
}
